import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';

import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
import L from 'leaflet';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});
import { 
  Home, 
  MapPin, 
  CreditCard, 
  User,
  ShoppingCart,
  Star,
  Gift,
  Clock,
  Phone,
  Navigation,
  Settings,
  LogOut,
  Mail,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';

// Mock data
const mockUser = {
  name: 'Jan Janssens',
  email: 'jan@example.com',
  points: 1250,
  level: 'Gold',
  memberSince: '2023',
  totalOrders: 45,
  favoriteLocation: 'Poule & Poulette Antwerpen'
};

const mockRestaurants = [
  {
    id: 1,
    name: 'Poule & Poulette Antwerpen Centrum',
    address: 'Meir 15, 2000 Antwerpen',
    distance: '0.8 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Drive-thru', 'Delivery', 'Takeaway'],
    phone: '+32 3 123 45 67',
    lat: 51.2194,
    lng: 4.4025
  },
  {
    id: 2,
    name: 'Poule & Poulette Gent Centrum',
    address: 'Veldstraat 25, 9000 Gent',
    distance: '2.1 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Delivery', 'Takeaway'],
    phone: '+32 9 123 45 67',
    lat: 51.0500,
    lng: 3.7300
  },
  {
    id: 3,
    name: 'Poule & Poulette Brussel Centrum',
    address: 'Nieuwstraat 10, 1000 Brussel',
    distance: '5.3 km',
    status: 'Closed',
    hours: '11:00 - 22:00',
    features: ['Takeaway'],
    phone: '+32 2 123 45 67',
    lat: 50.8503,
    lng: 4.3517
  },
  {
    id: 4,
    name: 'Poule & Poulette Leuven',
    address: 'Bondgenotenlaan 45, 3000 Leuven',
    distance: '8.7 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Drive-thru', 'Delivery', 'Takeaway'],
    phone: '+32 16 123 45 67',
    lat: 50.8798,
    lng: 4.7005
  },
  {
    id: 5,
    name: 'Poule & Poulette Brugge',
    address: 'Steenstraat 12, 8000 Brugge',
    distance: '12.4 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Delivery', 'Takeaway'],
    phone: '+32 50 123 45 67',
    lat: 51.2093,
    lng: 3.2247
  },
  {
    id: 6,
    name: 'Poule & Poulette Hasselt',
    address: 'Maastrichterstraat 78, 3500 Hasselt',
    distance: '15.2 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Drive-thru', 'Takeaway'],
    phone: '+32 11 123 45 67',
    lat: 50.9307,
    lng: 5.3325
  },
  {
    id: 7,
    name: 'Poule & Poulette Mechelen',
    address: 'Grote Markt 23, 2800 Mechelen',
    distance: '18.9 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Delivery', 'Takeaway'],
    phone: '+32 15 123 45 67',
    lat: 51.0259,
    lng: 4.4776
  },
  {
    id: 8,
    name: 'Poule & Poulette Aalst',
    address: 'Grote Markt 8, 9300 Aalst',
    distance: '22.1 km',
    status: 'Closed',
    hours: '11:00 - 22:00',
    features: ['Takeaway'],
    phone: '+32 53 123 45 67',
    lat: 50.9378,
    lng: 4.0400
  }
];

const mockRewards = [
  {
    id: 1,
    name: 'Gratis Friet',
    description: 'Gratis portie friet bij elke bestelling',
    points: 100,
    available: true
  },
  {
    id: 2,
    name: '2e Kip 50% Korting',
    description: '50% korting op je tweede kip bestelling',
    points: 200,
    available: true
  },
  {
    id: 3,
    name: 'Gratis Dessert',
    description: 'Gratis dessert naar keuze',
    points: 150,
    available: true
  }
];

// Authentication context
const AuthContext = React.createContext({
  isAuthenticated: false,
  login: (_email: string, _password: string) => Promise.resolve(false),
  logout: () => {},
  user: null as any
});

// Components
const Header = () => (
  <header className="header">
    <h1>POULE & POULETTE</h1>
    <p>REWARDS</p>
  </header>
);

const BottomNavigation = () => {
  const location = useLocation();
  const { isAuthenticated } = React.useContext(AuthContext);
  
  if (!isAuthenticated) return null;
  
  return (
    <nav className="bottom-nav">
      <Link to="/" className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}>
        <Home />
        <span>Home</span>
      </Link>
      <Link to="/restaurants" className={`nav-item ${location.pathname === '/restaurants' ? 'active' : ''}`}>
        <MapPin />
        <span>Restaurants</span>
      </Link>
      <Link to="/loyalty" className={`nav-item ${location.pathname === '/loyalty' ? 'active' : ''}`}>
        <CreditCard />
        <span>Punten</span>
      </Link>
      <Link to="/profile" className={`nav-item ${location.pathname === '/profile' ? 'active' : ''}`}>
        <User />
        <span>Profiel</span>
      </Link>
    </nav>
  );
};

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = React.useContext(AuthContext);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await login(email, password);
      if (!success) {
        setError('Ongeldige email of wachtwoord');
      }
    } catch (err) {
      setError('Er is een fout opgetreden');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Poule & Poulette</h1>
          <p>Log in op je account</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label className="form-label">Email</label>
            <div className="input-group">
              <Mail size={20} className="input-icon" />
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jan@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Wachtwoord</label>
            <div className="input-group">
              <Lock size={20} className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Inloggen...' : 'Inloggen'}
          </button>
        </form>

        <div className="login-footer">
          <p>Nog geen account? <a href="#" className="text-primary">Registreer hier</a></p>
          <p><a href="#" className="text-secondary">Wachtwoord vergeten?</a></p>
        </div>
      </div>
    </div>
  );
};

const HomeScreen = () => (
  <div className="main-content">
    <div className="points-display">
      <div className="points-number">{mockUser.points}</div>
      <div>Punten</div>
      <div className="loyalty-level loyalty-gold">{mockUser.level}</div>
    </div>

    <div className="grid grid-2">
      <button className="btn btn-primary">
        <ShoppingCart />
        <span>Bestel Nu</span>
      </button>
      <button className="btn btn-outline">
        <Star />
        <span>Mijn Punten</span>
      </button>
    </div>

    <div className="card">
      <h3>Speciale Aanbiedingen</h3>
      <div className="mt-16">
        <div className="p-16 bg-primary text-white rounded special-offer">
          <h4>🎉 2e Kip 50% Korting!</h4>
          <p>Geldig tot 31 december 2024</p>
        </div>
      </div>
    </div>

    <div className="card">
      <h3>Recente Bestellingen</h3>
      <div className="mt-16">
        <div className="p-16 border rounded">
          <div className="flex justify-between">
            <span>Kip Menu + Friet</span>
            <span className="text-primary">+25 punten</span>
          </div>
          <div className="text-secondary text-sm">2 dagen geleden</div>
        </div>
      </div>
    </div>
  </div>
);

const RestaurantsScreen = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);

  const filteredRestaurants = mockRestaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurant.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || 
                         (filter === 'open' && restaurant.status === 'Open') ||
                         (filter === 'drive-thru' && restaurant.features.includes('Drive-thru')) ||
                         (filter === 'delivery' && restaurant.features.includes('Delivery'));
    
    return matchesSearch && matchesFilter;
  });

  const handleRestaurantClick = (restaurant: any) => {
    setSelectedRestaurant(restaurant);
    console.log('Restaurant clicked:', restaurant.name);
  };

  // Debug logging
  useEffect(() => {
    console.log('Filtered restaurants:', filteredRestaurants);
    console.log('Restaurants with coordinates:', filteredRestaurants.map(r => ({
      name: r.name,
      lat: r.lat,
      lng: r.lng
    })));
  }, [filteredRestaurants]);

  return (
    <div className="restaurants-page">
      {/* Header */}
              <div className="restaurants-header">
          <h2>VIND EEN POULE & POULETTE RESTAURANT</h2>
          <p>Ontdek onze restaurants in heel België</p>
        </div>

      {/* Search and Filters */}
      <div className="restaurants-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Zoek op stad, postcode of adres..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filters-container">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Alle restaurants
          </button>
          <button 
            className={`filter-btn ${filter === 'open' ? 'active' : ''}`}
            onClick={() => setFilter('open')}
          >
            Nu open
          </button>
          <button 
            className={`filter-btn ${filter === 'drive-thru' ? 'active' : ''}`}
            onClick={() => setFilter('drive-thru')}
          >
            Drive-thru
          </button>
          <button 
            className={`filter-btn ${filter === 'delivery' ? 'active' : ''}`}
            onClick={() => setFilter('delivery')}
          >
            Delivery
          </button>
        </div>
      </div>

      {/* Main Content - Map and List Side by Side */}
      <div className="restaurants-layout">
        {/* Map Section */}
        <div className="map-section">
          <div className="map-container">
            <div className="map-header">
              <span>{filteredRestaurants.length} restaurants gevonden</span>
            </div>
            <MapContainer
              center={[50.8503, 4.3517]}
              zoom={8}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
              scrollWheelZoom={true}
              doubleClickZoom={true}
              dragging={true}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                maxZoom={19}
              />
              {filteredRestaurants.map(restaurant => (
                <Marker
                  key={restaurant.id}
                  position={[restaurant.lat, restaurant.lng]}
                  eventHandlers={{
                    click: () => handleRestaurantClick(restaurant)
                  }}
                >
                  <Popup>
                    <div className="restaurant-popup">
                      <h3>{restaurant.name}</h3>
                      <p><strong>Adres:</strong> {restaurant.address}</p>
                      <p><strong>Status:</strong> <span style={{color: restaurant.status === 'Open' ? '#27AE60' : '#E74C3C', fontWeight: 'bold'}}>{restaurant.status}</span></p>
                      <p><strong>Openingstijden:</strong> {restaurant.hours}</p>
                      <p><strong>Afstand:</strong> {restaurant.distance}</p>
                      <p><strong>Features:</strong> {restaurant.features.join(', ')}</p>
                      <div className="popup-actions">
                        <button onClick={() => window.open(`tel:${restaurant.phone}`)}>
                          <Phone size={14} /> Bellen
                        </button>
                        <button onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}`)}>
                          <Navigation size={14} /> Route
                        </button>
                        <button onClick={() => alert('Bestellen functionaliteit komt binnenkort!')}>
                          <ShoppingCart size={14} /> Bestellen
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Restaurant List Section */}
        <div className="restaurants-list-section">
          <div className="list-header">
            <h3>Restaurants ({filteredRestaurants.length})</h3>
          </div>
          <div className="restaurants-list">
            {filteredRestaurants.map(restaurant => (
              <div 
                key={restaurant.id} 
                className={`restaurant-card ${selectedRestaurant?.id === restaurant.id ? 'selected' : ''}`}
                onClick={() => handleRestaurantClick(restaurant)}
              >
                <div className="restaurant-info">
                  <div className="restaurant-main">
                    <h4>{restaurant.name}</h4>
                    <p className="restaurant-address">{restaurant.address}</p>
                    <p className="restaurant-distance">{restaurant.distance}</p>
                  </div>
                  <div className="restaurant-status">
                    <span className={`status-badge ${restaurant.status === 'Open' ? 'open' : 'closed'}`}>
                      {restaurant.status}
                    </span>
                  </div>
                </div>
                
                <div className="restaurant-details">
                  <div className="restaurant-hours">
                    <Clock size={14} />
                    <span>{restaurant.hours}</span>
                  </div>
                  <div className="restaurant-features">
                    {restaurant.features.map(feature => (
                      <span key={feature} className="feature-badge">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="restaurant-actions">
                  <button className="action-btn" onClick={(e) => { e.stopPropagation(); window.open(`tel:${restaurant.phone}`); }}>
                    <Phone size={16} />
                    <span>Bellen</span>
                  </button>
                  <button className="action-btn" onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}`); }}>
                    <Navigation size={16} />
                    <span>Route</span>
                  </button>
                  <button className="action-btn" onClick={(e) => { e.stopPropagation(); alert('Bestellen functionaliteit komt binnenkort!'); }}>
                    <ShoppingCart size={16} />
                    <span>Bestellen</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const LoyaltyScreen = () => {
  const [activeTab, setActiveTab] = useState('rewards');

  return (
    <div className="main-content">
      <div className="points-display">
        <div className="points-number">{mockUser.points}</div>
        <div>Punten</div>
        <div className="loyalty-level loyalty-gold">{mockUser.level}</div>
      </div>

      <div className="card">
        <h3>Voortgang naar Volgend Niveau</h3>
        <div className="mt-16">
          <div className="flex justify-between mb-8">
            <span>Huidig: {mockUser.level}</span>
            <span>Volgend: Platinum</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-primary h-2 rounded-full" style={{ width: '75%' }}></div>
          </div>
          <p className="text-secondary text-sm mt-8">Nog 250 punten nodig voor Platinum</p>
        </div>
      </div>

      <div className="grid grid-3 mb-16">
        <button 
          className={`btn ${activeTab === 'rewards' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('rewards')}
        >
          Mijn Beloningen
        </button>
        <button 
          className={`btn ${activeTab === 'available' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('available')}
        >
          Beschikbaar
        </button>
        <button 
          className={`btn ${activeTab === 'history' ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setActiveTab('history')}
        >
          Geschiedenis
        </button>
      </div>

      {activeTab === 'available' && (
        <div>
          {mockRewards.map(reward => (
            <div key={reward.id} className="card">
              <div className="flex justify-between items-start">
                <div>
                  <h3>{reward.name}</h3>
                  <p className="text-secondary">{reward.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-primary font-bold">{reward.points} punten</div>
                  <button className="btn btn-primary mt-8">
                    <Gift size={16} />
                    <span>Inwisselen</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'rewards' && (
        <div className="card">
          <h3>Mijn Actieve Beloningen</h3>
          <p className="text-secondary">Je hebt momenteel geen actieve beloningen.</p>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card">
          <h3>Transactie Geschiedenis</h3>
          <div className="mt-16">
            <div className="p-16 border rounded mb-8">
              <div className="flex justify-between">
                <span>Gratis Friet ingewisseld</span>
                <span className="text-primary">-100 punten</span>
              </div>
              <div className="text-secondary text-sm">1 week geleden</div>
            </div>
            <div className="p-16 border rounded">
              <div className="flex justify-between">
                <span>Bestelling: Kip Menu</span>
                <span className="text-primary">+25 punten</span>
              </div>
              <div className="text-secondary text-sm">2 dagen geleden</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileScreen = () => {
  const { logout } = React.useContext(AuthContext);

  return (
    <div className="main-content">
      <div className="card text-center">
        <div className="w-20 h-20 bg-primary rounded-full mx-auto mb-16 flex items-center justify-center">
          <User size={32} color="white" />
        </div>
        <h2>{mockUser.name}</h2>
        <p className="text-secondary">{mockUser.email}</p>
        <p className="text-secondary">Lid sinds {mockUser.memberSince}</p>
      </div>

      <div className="grid grid-3 mb-16">
        <div className="card text-center">
          <div className="text-2xl font-bold text-primary">{mockUser.totalOrders}</div>
          <div className="text-secondary">Bestellingen</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-primary">{mockUser.points}</div>
          <div className="text-secondary">Punten</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-primary">{mockUser.level}</div>
          <div className="text-secondary">Niveau</div>
        </div>
      </div>

      <div className="card">
        <h3>Favoriete Locatie</h3>
        <p className="text-secondary">{mockUser.favoriteLocation}</p>
      </div>

      <div className="card">
        <h3>Instellingen</h3>
        <div className="space-y-16">
          <div className="flex justify-between items-center">
            <span>Persoonlijke Gegevens</span>
            <Settings size={16} />
          </div>
          <div className="flex justify-between items-center">
            <span>Bestelgeschiedenis</span>
            <Clock size={16} />
          </div>
          <div className="flex justify-between items-center">
            <span>Betaalmethoden</span>
            <CreditCard size={16} />
          </div>
          <div className="flex justify-between items-center">
            <span>Adressen</span>
            <MapPin size={16} />
          </div>
          <div className="flex justify-between items-center">
            <span>Notificaties</span>
            <input type="checkbox" defaultChecked />
          </div>
          <div className="flex justify-between items-center">
            <span>Locatie Services</span>
            <input type="checkbox" defaultChecked />
          </div>
          <div className="flex justify-between items-center">
            <span>Marketing Communicatie</span>
            <input type="checkbox" />
          </div>
          <div className="flex justify-between items-center">
            <span>Help & Ondersteuning</span>
            <span>→</span>
          </div>
          <div className="flex justify-between items-center">
            <span>Over de App</span>
            <span>→</span>
          </div>
          <div className="flex justify-between items-center">
            <span>Privacy & Beleid</span>
            <span>→</span>
          </div>
          <div className="flex justify-between items-center text-red-500" onClick={logout}>
            <span>Uitloggen</span>
            <LogOut size={16} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = React.useContext(AuthContext);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user] = useState(mockUser);

  const login = async (email: string, password: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (email === 'jan@example.com' && password === 'password') {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, user }}>
      <Router>
        <div className="app">
          {isAuthenticated && <Header />}
          <Routes>
            <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginScreen />} />
            <Route path="/" element={<ProtectedRoute><HomeScreen /></ProtectedRoute>} />
            <Route path="/restaurants" element={<ProtectedRoute><RestaurantsScreen /></ProtectedRoute>} />
            <Route path="/loyalty" element={<ProtectedRoute><LoyaltyScreen /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
          </Routes>
          <BottomNavigation />
        </div>
      </Router>
    </AuthContext.Provider>
  );
};

export default App;
